import { useLiveQuery } from '@electric-sql/pglite-react';
import {
  AbnormalColor,
  type BrandSettings,
  can,
  computeSection,
  DEFAULT_BRAND,
  effectivePrint,
  isEditable,
  isFinalLike,
  nextStatuses,
  printsSignatures,
  type ReportContent,
  ReportContent as ReportContentSchema,
  type ReportSection,
  type ReportStatus,
  sampleSummary,
  summarizeReport,
  type TemplateDoc,
} from '@quickreport/domain';
import { Badge, Button, PillTabs } from '@quickreport/ui';
import {
  getSite,
  loadReport,
  ReportBlockedError,
  type ReportRecord,
  renameReport,
  type SiteInfo,
  saveReportContent,
  transitionReport,
  WorkflowError,
} from '@quickreport/workspace';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Download,
  FileClock,
  Lock,
  Printer,
  Receipt,
  Send,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { canonical } from '../../lib/canonical';
import { isDesktop } from '../../lib/desktop';
import { loadProfile, printPdf } from '../../lib/desktop-print';
import { useWorkspace } from '../../lib/workspace';
import { buildSignoff, loadBrand, saveBrand } from '../../local-db/settings';
import { PdfPages } from '../../pdf/PdfPages';
import { GridEditor } from '../templates/GridEditor';
import { RowPanel } from '../templates/RowPanel';
import { STATUS_LABEL, STATUS_TONE, useCtx } from './context';
import { DeleteReportDialog } from './DeleteReportDialog';
import { DetailsPanel } from './DetailsPanel';
import { DueControl } from './DueControl';
import { ReasonDialog, type SignatoryOption, SignatureNeededDialog, SignDialog } from './dialogs';
import { HistoryPanel } from './HistoryPanel';
import { PaymentPanel } from './PaymentPanel';
import { ResultTable } from './ResultTable';
import { RichEditor } from './RichEditor';
import { renderReportPdf } from './report-pdf';
import { SectionTabs } from './SectionTabs';
import { SendDialog } from './SendDialog';
import { SlipDialog } from './SlipDialog';
import { VisitReports } from './VisitReports';

const SAVE_DELAY_MS = 600;
/** Background syncs wait this long after the last keystroke. */
const SYNC_QUIET_MS = 3000;
/** A copy of the report from elsewhere is shown only after this long without typing. */
const IDLE_BEFORE_REPLACE_MS = 3000;

export function ReportEditorPage() {
  const { reportId } = useParams({ from: '/reports/$reportId' });
  const ws = useWorkspace();
  // Re-run when the stored row changes (e.g. the server issues the verification code).
  const live = useLiveQuery<{ updated_at: string; verify_code: string | null; status: string }>(
    'select updated_at, verify_code, status from reports where id = $1',
    [reportId],
  );
  const [record, setRecord] = useState<ReportRecord | null | undefined>(undefined);
  const [site, setSite] = useState<SiteInfo | null>(null);
  const stamp = live?.rows[0]
    ? `${live.rows[0].updated_at}|${live.rows[0].verify_code}|${live.rows[0].status}`
    : '';

  // biome-ignore lint/correctness/useExhaustiveDependencies: `stamp` is the signal that the row changed and must be reloaded
  useEffect(() => {
    let alive = true;
    loadReport(ws.db, reportId).then(async (r) => {
      if (!alive) return;
      setRecord(r);
      if (r) setSite(await getSite(ws.db, r.siteId).catch(() => null));
    });
    return () => {
      alive = false;
    };
  }, [ws.db, reportId, stamp]);

  if (record === undefined) return <div className="p-8 text-slate">Opening report…</div>;
  if (record === null || !site) {
    return (
      <div className="p-8">
        <p>This report is not on this computer.</p>
        <Link to="/reports" className="text-brand hover:underline">
          Back to reports
        </Link>
      </div>
    );
  }
  return <Editor key={record.id} record={record} site={site} />;
}

type Side = 'report' | 'pdf' | 'payment' | 'history';

function Editor({ record, site }: { record: ReportRecord; site: SiteInfo }) {
  const ws = useWorkspace();
  const ctx = useCtx();
  const [content, setContent] = useState<ReportContent>(record.content);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [side, setSide] = useState<Side>('report');
  const [sectionIdx, setSectionIdx] = useState(0);
  const [dialog, setDialog] = useState<
    'sign' | 'partial' | 'signature' | 'amend' | 'revoke' | 'send' | 'slip' | 'delete' | null
  >(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [name, setName] = useState(record.name);
  const [selectedRow, setSelectedRow] = useState<{ section: number; rowId: string } | null>(null);

  const status = record.status;
  const editable =
    isEditable(status) && (can(ctx.roles, 'enter_results') || can(ctx.roles, 'register_visit'));
  const canEnterResults = isEditable(status) && can(ctx.roles, 'enter_results');
  // Anyone on the floor can delete work in progress; a signed report only the owner or lab admin.
  const canDelete =
    can(ctx.roles, 'register_visit') && (!isFinalLike(status) || can(ctx.roles, 'delete_signed'));

  // The screen is only replaced by a report that really came from somewhere else (another
  // computer), never by our own saves coming back, and never while the person is typing. Our own
  // saves are recognised by what they say, not by when they arrive: a copy of the report read just
  // before the latest save is stale, and putting it on screen is what used to wipe a value and
  // send the cursor back to the start.
  const saving = useRef(false);
  const dirtyRef = useRef(false);
  const lastKeyAt = useRef(0);
  const ownSaves = useRef<string[]>([canonical(record.content)]);
  const incoming = useRef(record);
  incoming.current = record;
  const recheck = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `latest` is a ref read on purpose
  const adopt = useCallback(() => {
    clearTimeout(recheck.current);
    if (dirtyRef.current || saving.current) return;
    const theirs = canonical(incoming.current.content);
    if (theirs === canonical(latest.current)) return; // already what is on screen
    if (ownSaves.current.includes(theirs)) return; // one of our own saves, arriving late
    // Someone else changed it. Wait until typing has stopped, then show it.
    const quiet = Date.now() - lastKeyAt.current;
    if (quiet < IDLE_BEFORE_REPLACE_MS) {
      recheck.current = setTimeout(adopt, IDLE_BEFORE_REPLACE_MS - quiet + 50);
      return;
    }
    ownSaves.current.push(theirs);
    setContent(incoming.current.content);
  }, []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: run when a new copy of the report arrives
  useEffect(() => {
    adopt();
  }, [record, adopt]);
  useEffect(() => () => clearTimeout(recheck.current), []);
  useEffect(() => setName(record.name), [record.name]);

  const computed = useMemo(() => {
    return content.sections.map((s) =>
      computeSection(
        s.doc,
        s.values,
        {
          ...(content.patient.sex ? { sex: content.patient.sex } : {}),
          ...(content.patient.ageDays !== undefined ? { ageDays: content.patient.ageDays } : {}),
        },
        s.narratives ? { narratives: s.narratives } : {},
      ),
    );
  }, [content]);
  const summary = useMemo(() => summarizeReport(content), [content]);

  // The lab's own choice for how out-of-range values print: the starting point for this report.
  const brandRow = useLiveQuery<{
    doc: { abnormalBold?: unknown; abnormalColor?: unknown } | null;
  }>(
    'select doc from brand_settings where org_id = $1 and site_id is null and deleted_at is null limit 1',
    [ws.orgId],
  );
  const labLook = useMemo(() => {
    const d = brandRow?.rows[0]?.doc;
    const color = AbnormalColor.safeParse(d?.abnormalColor);
    return {
      abnormalBold: typeof d?.abnormalBold === 'boolean' ? d.abnormalBold : undefined,
      abnormalColor: color.success ? color.data : undefined,
    };
  }, [brandRow]);

  // Autosave shortly after typing stops.
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const latest = useRef(content);
  latest.current = content;
  const persist = useCallback(async (): Promise<void> => {
    if (saving.current) return;
    saving.current = true;
    const snapshot = latest.current;
    try {
      const parsed = ReportContentSchema.parse(snapshot);
      await saveReportContent(ws.db, ctx, record.id, parsed);
      ownSaves.current.push(canonical(parsed));
      if (ownSaves.current.length > 40) ownSaves.current.shift();
      setSavedAt(new Date().toISOString());
      setError(null);
      // Only call the report saved if nothing was typed while it was being saved.
      if (latest.current === snapshot) {
        dirtyRef.current = false;
        setDirty(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      saving.current = false;
    }
    if (latest.current !== snapshot) return persist();
  }, [ws.db, ctx, record.id]);

  const change = useCallback(
    (next: ReportContent | ((c: ReportContent) => ReportContent)) => {
      setContent((c) => (typeof next === 'function' ? next(c) : next));
      dirtyRef.current = true;
      lastKeyAt.current = Date.now();
      // Background syncs wait until typing has stopped for a few seconds.
      ws.engine.hold(SYNC_QUIET_MS);
      setDirty(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => void persist(), SAVE_DELAY_MS);
    },
    [persist, ws.engine],
  );
  useEffect(() => () => clearTimeout(timer.current), []);

  // Save before doing anything that reads the stored report.
  async function flush() {
    clearTimeout(timer.current);
    // A save already running may not include the latest typing, so wait and save again.
    while (saving.current) await new Promise((r) => setTimeout(r, 20));
    if (dirtyRef.current) await persist();
  }

  const setSection = (i: number, patch: (s: ReportSection) => ReportSection) =>
    change((c) => ({ ...c, sections: c.sections.map((s, j) => (j === i ? patch(s) : s)) }));

  // ---- workflow ------------------------------------------------------------------
  const navigate = useNavigate();
  const next = nextStatuses(site.workflowLevel, status, ctx.roles);
  const [signatories, setSignatories] = useState<SignatoryOption[]>([]);
  useEffect(() => {
    ws.db
      .query<SignatoryOption>(
        'select id, name, qualification, member_id from signatories where org_id = $1 and deleted_at is null order by name',
        [ws.orgId],
      )
      .then((r) => setSignatories(r.rows));
  }, [ws.db, ws.orgId]);
  // Signatures are only required when the lab prints them. Until the settings are read, assume so.
  const [brand, setBrand] = useState<BrandSettings>(DEFAULT_BRAND);
  // biome-ignore lint/correctness/useExhaustiveDependencies: read again whenever a dialog closes
  useEffect(() => {
    void loadBrand(ws.db, ws.orgId, { labName: ws.auth.orgName }).then(setBrand);
  }, [ws.db, ws.orgId, ws.auth.orgName, dialog]);
  const needsSignatory = printsSignatures(brand) && signatories.length === 0;
  const [signFor, setSignFor] = useState<'sign' | 'partial'>('sign');
  /** Open the sign dialog, or say what is missing first. The button itself is never disabled. */
  const startSigning = (kind: 'sign' | 'partial') => {
    setSignFor(kind);
    setDialog(needsSignatory ? 'signature' : kind);
  };
  async function turnSignaturesOff() {
    const b = await loadBrand(ws.db, ws.orgId, { labName: ws.auth.orgName });
    const keep = effectivePrint(b);
    const next = {
      ...b,
      printAll: false,
      letterhead: true,
      print: { ...keep, signatureImage: false, signatoryNames: false },
    };
    await saveBrand(ws.db, ws.orgId, next);
    setBrand(next);
    void ws.engine.syncNow();
    setDialog(signFor);
  }
  const mySignatory =
    signatories.find((s) => s.member_id === ws.auth.memberId)?.id ??
    (signatories.length === 1 ? signatories[0]?.id : null) ??
    null;

  async function move(to: ReportStatus, opts: Parameters<typeof transitionReport>[4] = {}) {
    setDialogError(null);
    setError(null);
    try {
      await flush();
      await transitionReport(ws.db, ctx, record.id, to, opts);
      setDialog(null);
      await ws.engine.refreshCounts();
      void ws.engine.syncNow();
    } catch (err) {
      const msg =
        err instanceof ReportBlockedError
          ? err.issues.join(' · ')
          : err instanceof Error
            ? err.message
            : 'Could not do that';
      if (dialog) setDialogError(msg);
      else setError(msg);
      if (!(err instanceof WorkflowError)) console.error(err);
    }
  }

  async function rename() {
    if (name.trim() && name.trim() !== record.name) {
      try {
        await renameReport(ws.db, ctx, record.id, name);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not rename');
      }
    }
  }

  const actions: { to: ReportStatus; label: string; primary?: boolean; run: () => void }[] = [];
  if (next.includes('awaiting_verification'))
    actions.push({
      to: 'awaiting_verification',
      label: 'Send for verification',
      primary: true,
      run: () => void move('awaiting_verification'),
    });
  if (next.includes('awaiting_signature')) {
    actions.push({
      to: 'awaiting_signature',
      label:
        status === 'awaiting_verification' ? 'Verified: send for signature' : 'Send for signature',
      primary: !next.includes('final'),
      run: () => void move('awaiting_signature'),
    });
  }
  if (next.includes('final'))
    actions.push({
      to: 'final',
      label: 'Sign and PDF',
      primary: true,
      run: () => startSigning('sign'),
    });
  if (next.includes('partial'))
    actions.push({ to: 'partial', label: 'Release partial', run: () => startSigning('partial') });
  if (next.includes('draft') && status !== 'draft')
    actions.push({ to: 'draft', label: 'Return for correction', run: () => void move('draft') });
  if (next.includes('amended'))
    actions.push({ to: 'amended', label: 'Amend', run: () => setDialog('amend') });
  if (next.includes('revoked'))
    actions.push({ to: 'revoked', label: 'Revoke', run: () => setDialog('revoke') });

  const generic = content.kind === 'generic';
  const selDoc: { doc: TemplateDoc; index: number } | null = selectedRow
    ? { doc: content.sections[selectedRow.section]?.doc as TemplateDoc, index: selectedRow.section }
    : null;
  const selTest = selDoc?.doc.rows.find((r) => r.id === selectedRow?.rowId);

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-2 border-b border-line bg-paper px-4 py-2.5">
        <Link
          to="/reports"
          className="rounded p-1.5 text-slate hover:bg-canvas"
          aria-label="Back to reports"
        >
          <ArrowLeft size={18} />
        </Link>
        <div className="min-w-0 flex-1">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={rename}
            disabled={!isEditable(status) && !can(ctx.roles, 'register_visit')}
            aria-label="Report name"
            className="w-full min-w-0 rounded border border-transparent px-1.5 py-0.5 text-[17px] font-semibold hover:border-line focus:border-brand focus:outline-none"
          />
          <div className="flex flex-wrap items-center gap-2 px-1.5 text-[12px] text-slate">
            <span className="font-mono">{record.labNo}</span>
            <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{STATUS_LABEL[status] ?? status}</Badge>
            {record.version > 0 ? <span>Version {record.version}</span> : null}
            <DueControl
              reportId={record.id}
              dueDate={record.dueDate}
              dueDoneAt={record.dueDoneAt}
              status={status}
            />
            {record.verifyCode ? (
              <span className="inline-flex items-center gap-1 text-ok">
                <CheckCircle2 size={12} /> QR ready
              </span>
            ) : status === 'final' || status === 'partial' ? (
              <span
                className="inline-flex items-center gap-1 text-warn"
                title="The QR code is added after this report reaches the server"
              >
                <FileClock size={12} /> No QR yet
              </span>
            ) : null}
            {record.content.sections.some((s) => s.doc.sensitive) ? (
              <span className="inline-flex items-center gap-1">
                <Lock size={12} /> Sensitive
              </span>
            ) : null}
            <span aria-live="polite">{dirty ? 'Saving…' : savedAt ? 'Saved' : ''}</span>
          </div>
        </div>
        {/* On a phone the buttons get a row of their own, so the report's name keeps its width. */}
        <div className="flex w-full flex-wrap items-center gap-2 md:contents">
          {can(ctx.roles, 'send_report') && (status === 'final' || status === 'partial') ? (
            <Button
              size="sm"
              className="max-md:h-10 max-md:flex-1"
              onClick={() => setDialog('send')}
            >
              <Send size={14} /> Send
            </Button>
          ) : null}
          <Button size="sm" className="max-md:h-10 max-md:flex-1" onClick={() => setDialog('slip')}>
            <Receipt size={14} /> Slip
          </Button>
          {canDelete ? (
            <Button
              tone="danger"
              size="sm"
              className="max-md:h-10 max-md:flex-1"
              onClick={() => setDialog('delete')}
            >
              <Trash2 size={14} /> Delete
            </Button>
          ) : null}
          {actions.map((a) => (
            <Button
              key={a.to + a.label}
              tone={a.primary ? 'primary' : a.to === 'revoked' ? 'danger' : 'secondary'}
              size="sm"
              className="max-md:h-10 max-md:flex-1"
              onClick={a.run}
            >
              {a.label}
            </Button>
          ))}
        </div>
      </header>

      {error ? (
        <div
          role="alert"
          className="border-b border-red-200 bg-red-50 px-4 py-1.5 text-[13px] text-flag"
        >
          {error}
        </div>
      ) : null}
      {!isEditable(status) ? (
        <div className="border-b border-line bg-canvas px-4 py-1.5 text-[13px] text-slate">
          {status === 'revoked'
            ? 'This report was revoked. It stays on record but cannot be changed.'
            : 'This report is signed and cannot be edited. Use Amend to correct it; the change is recorded.'}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto">
        <details className="group border-b border-line bg-paper">
          <summary className="flex cursor-pointer select-none items-center gap-3 px-4 py-2 text-[13px]">
            <span className="font-semibold text-navy">Patient and visit details</span>
            <span className="min-w-0 flex-1 truncate text-slate">
              {[
                content.patient.name,
                content.patient.ageText,
                content.patient.sex === 'M' ? 'Male' : content.patient.sex === 'F' ? 'Female' : '',
                content.samples.length ? sampleSummary(content.samples) : content.visit.sampleType,
                content.visit.referredBy ? `Referred by ${content.visit.referredBy}` : '',
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
            <span className="text-[12px] text-brand group-open:hidden">Show</span>
            <span className="hidden text-[12px] text-brand group-open:inline">Hide</span>
          </summary>
          <DetailsPanel content={content} editable={editable} onChange={(c) => change(c)} />
        </details>

        <div className="px-4 pt-3">
          <PillTabs
            label="Report sections"
            value={side}
            onChange={setSide}
            tabs={[
              { value: 'report', label: 'Report' },
              { value: 'pdf', label: 'PDF' },
              ...(ws.can('view_billing') ? [{ value: 'payment' as const, label: 'Payment' }] : []),
              { value: 'history', label: 'History' },
            ]}
          />
        </div>

        {side === 'report' ? (
          <div className="p-4">
            {generic && selTest?.kind === 'test' && selDoc ? (
              <div className="mb-4 rounded-lg border border-line bg-paper">
                <RowPanel
                  doc={selDoc.doc}
                  row={selTest}
                  onChange={(fn) => setSection(selDoc.index, (x) => ({ ...x, doc: fn(x.doc) }))}
                  onClose={() => setSelectedRow(null)}
                />
              </div>
            ) : null}
            <VisitReports
              visitId={record.visitId}
              currentId={record.id}
              liveContent={content}
              beforeLeave={flush}
            />
            <SummaryStrip summary={summary} status={status} />
            {content.sections.length > 1 ? (
              <div className="mt-4">
                <SectionTabs
                  active={Math.min(sectionIdx, content.sections.length - 1)}
                  onChange={setSectionIdx}
                  tabs={content.sections.map((s, i) => {
                    const rows = computed[i]?.rows.filter((r) => r.kind === 'test') ?? [];
                    return {
                      key: `${s.templateId ?? 'generic'}-${i}`,
                      label: s.doc.name,
                      total: rows.length,
                      done: rows.filter((r) => r.kind === 'test' && r.display !== '').length,
                      flagged: computed[i]?.flagged.length ?? 0,
                    };
                  })}
                />
              </div>
            ) : null}
            <div className="mt-4 grid gap-6">
              {content.sections.map((s, i) =>
                i !== Math.min(sectionIdx, content.sections.length - 1) ? null : (
                  // biome-ignore lint/suspicious/noArrayIndexKey: sections are positional and have no id of their own
                  <section key={`${s.templateId ?? 'generic'}-${i}`} aria-label={s.doc.name}>
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <h2 className="text-[15px] font-semibold">{s.doc.name}</h2>
                      {s.doc.department ? (
                        <span className="text-[12px] text-slate">{s.doc.department}</span>
                      ) : null}
                      <span className="flex-1" />
                      {canEnterResults ? (
                        <PerformedBy
                          section={s}
                          onChange={(p) => setSection(i, (x) => ({ ...x, performedBy: p }))}
                        />
                      ) : null}
                    </div>
                    {s.doc.extraFields.length ? (
                      <div className="mb-2 grid grid-cols-3 gap-3 rounded-lg border border-line bg-paper p-3">
                        {s.doc.extraFields.map((f) => (
                          <label
                            key={f.key}
                            className="grid gap-1 text-[12px] font-medium text-slate"
                          >
                            {f.label}
                            <input
                              type={
                                f.type === 'number'
                                  ? 'number'
                                  : f.type === 'datetime'
                                    ? 'datetime-local'
                                    : f.type === 'date'
                                      ? 'date'
                                      : 'text'
                              }
                              disabled={!canEnterResults}
                              value={s.extra?.[f.key] ?? ''}
                              onChange={(e) =>
                                setSection(i, (x) => ({
                                  ...x,
                                  extra: { ...x.extra, [f.key]: e.target.value },
                                }))
                              }
                              className="h-8 rounded-md border border-line px-2 text-[13px] font-normal text-ink"
                            />
                          </label>
                        ))}
                      </div>
                    ) : null}
                    {generic ? (
                      <GridEditor
                        doc={s.doc}
                        onChange={(fn) =>
                          setSection(i, (x) => ({
                            ...x,
                            doc: typeof fn === 'function' ? fn(x.doc) : fn,
                          }))
                        }
                        computed={computed[i] as NonNullable<(typeof computed)[number]>}
                        examples={s.values}
                        onExample={(rowId, v) =>
                          setSection(i, (x) => ({ ...x, values: { ...x.values, [rowId]: v } }))
                        }
                        selectedRowId={selectedRow?.section === i ? selectedRow.rowId : null}
                        onSelectRow={(id) => setSelectedRow(id ? { section: i, rowId: id } : null)}
                        onError={setError}
                      />
                    ) : (
                      <ResultTable
                        section={s}
                        computed={computed[i] as NonNullable<(typeof computed)[number]>}
                        editable={canEnterResults}
                        onValue={(rowId, v) =>
                          setSection(i, (x) => ({ ...x, values: { ...x.values, [rowId]: v } }))
                        }
                        onNarrative={(rowId, text) =>
                          setSection(i, (x) => ({
                            ...x,
                            narratives: { ...x.narratives, [rowId]: text },
                          }))
                        }
                        lab={labLook}
                        report={content.abnormal}
                        onStyle={(rowId, c) => {
                          const look = c.look
                            ? { bold: c.look.bold, color: c.look.color }
                            : undefined;
                          if (c.scope === 'report') {
                            // Every result in this report: one choice for the report, and none of
                            // the single results keeps a choice of its own.
                            change((r) => ({
                              ...r,
                              abnormal: look,
                              sections: r.sections.map((sec) => ({ ...sec, rowStyles: undefined })),
                            }));
                          } else {
                            setSection(i, (x) => {
                              const rest = { ...(x.rowStyles ?? {}) };
                              if (look) rest[rowId] = look;
                              else delete rest[rowId];
                              return {
                                ...x,
                                rowStyles: Object.keys(rest).length > 0 ? rest : undefined,
                              };
                            });
                          }
                        }}
                      />
                    )}
                    {s.doc.interpretation ? (
                      <p className="mt-2 rounded-md bg-canvas px-3 py-2 text-[13px] text-slate">
                        {s.doc.interpretation}
                      </p>
                    ) : null}
                  </section>
                ),
              )}
              {/* Every report has Comments, whatever its templates. It prints under the results. */}
              {editable || content.comments ? (
                <section aria-label="Comments" className="rounded-lg border border-line p-3">
                  <h3 className="text-[13px] font-semibold">Comments</h3>
                  <p className="mb-2 text-[12px] text-slate">
                    Optional. Printed under the results, above the signatures. Drag the corner of
                    the box to make it bigger.
                  </p>
                  <RichEditor
                    label="Comments"
                    readOnly={!editable}
                    value={content.comments ?? ''}
                    onChange={(md) =>
                      change((c) => {
                        const { comments: _old, ...rest } = c;
                        return md.trim() ? { ...rest, comments: md } : rest;
                      })
                    }
                  />
                </section>
              ) : null}
            </div>
          </div>
        ) : side === 'pdf' ? (
          <div className="h-[78vh] min-h-96">
            <PdfTab record={record} content={content} dirty={dirty} />
          </div>
        ) : side === 'payment' ? (
          <PaymentPanel reportId={record.id} />
        ) : (
          <HistoryPanel reportId={record.id} />
        )}
      </div>

      {dialog === 'delete' ? (
        <DeleteReportDialog
          reportId={record.id}
          onBeforeDelete={() => clearTimeout(timer.current)}
          onDeleted={() => void navigate({ to: '/reports' })}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'sign' || dialog === 'partial' ? (
        <SignDialog
          partial={dialog === 'partial'}
          summary={summary}
          signatories={signatories}
          defaultSignatory={mySignatory}
          error={dialogError}
          onCancel={() => {
            setDialog(null);
            setDialogError(null);
          }}
          onConfirm={async (signatoryId, criticalReviewed) => {
            const signoff = await buildSignoff(ws.db, ws.orgId, ws.auth.orgName, signatoryId);
            void move(dialog === 'partial' ? 'partial' : 'final', {
              ...(signatoryId ? { signatoryId } : {}),
              criticalReviewed,
              signoff,
            });
          }}
        />
      ) : null}
      {dialog === 'signature' ? (
        <SignatureNeededDialog
          canChange={ws.can('manage_site')}
          onCancel={() => setDialog(null)}
          onAdd={() => {
            setDialog(null);
            void navigate({ to: '/settings', hash: 'signatories' });
          }}
          onTurnOff={() => void turnSignaturesOff()}
        />
      ) : null}
      {dialog === 'send' ? (
        <SendDialog
          record={record}
          sensitive={content.sections.some((x) => x.doc.sensitive)}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'slip' ? (
        <SlipDialog visitId={record.visitId} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'amend' ? (
        <ReasonDialog
          title="Amend this report"
          prompt="Why does it need correcting? This is recorded with the new version."
          confirmLabel="Start amending"
          error={dialogError}
          onCancel={() => setDialog(null)}
          onConfirm={(reason) => void move('amended', { reason })}
        />
      ) : null}
      {dialog === 'revoke' ? (
        <ReasonDialog
          title="Revoke this report"
          prompt="Why is it being withdrawn? Anyone who scans its QR will see it was revoked."
          confirmLabel="Revoke report"
          danger
          error={dialogError}
          onCancel={() => setDialog(null)}
          onConfirm={(reason) => void move('revoked', { reason })}
        />
      ) : null}
    </div>
  );
}

function SummaryStrip({
  summary,
  status,
}: {
  summary: ReturnType<typeof summarizeReport>;
  status: ReportStatus;
}) {
  const blocking = [...new Set(summary.blocking)];
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-3 text-[13px]">
        <span className="text-slate">
          {summary.filledCount} of {summary.testCount} results entered
        </span>
        {summary.flagged.length ? (
          <span className="font-medium text-flag">{summary.flagged.length} outside range</span>
        ) : summary.filledCount > 0 ? (
          <span className="text-ok">All entered values are in range</span>
        ) : null}
        {summary.critical ? (
          <span className="inline-flex items-center gap-1 rounded bg-red-50 px-2 py-0.5 font-medium text-flag">
            <AlertTriangle size={13} /> {summary.critical} critical
          </span>
        ) : null}
      </div>
      {isEditable(status) && blocking.length ? (
        <div className="rounded-md bg-amber-50 px-3 py-2 text-[13px] text-warn" role="status">
          <b>Before this can be signed:</b> {blocking.slice(0, 4).join(' · ')}
          {blocking.length > 4 ? ` · and ${blocking.length - 4} more` : ''}
        </div>
      ) : null}
    </div>
  );
}

function PerformedBy({
  section,
  onChange,
}: {
  section: ReportSection;
  onChange: (p: ReportSection['performedBy']) => void;
}) {
  const p = section.performedBy;
  const out = p?.kind === 'reference_lab';
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <select
        aria-label="Where this test was done"
        value={out ? 'out' : 'in'}
        onChange={(e) =>
          onChange(
            e.target.value === 'out' ? { kind: 'reference_lab', name: '' } : { kind: 'in_house' },
          )
        }
        className="h-7 rounded-md border border-line bg-paper px-1.5"
      >
        <option value="in">Done in this lab</option>
        <option value="out">Sent to a reference lab</option>
      </select>
      {p?.kind === 'reference_lab' ? (
        <>
          <input
            aria-label="Reference lab name"
            placeholder="Lab name"
            value={p.name}
            onChange={(e) => onChange({ ...p, name: e.target.value })}
            className="h-7 w-40 rounded-md border border-line px-2"
          />
          <input
            aria-label="Accreditation"
            placeholder="NABL no."
            value={p.accreditation ?? ''}
            onChange={(e) => onChange({ ...p, accreditation: e.target.value || undefined })}
            className="h-7 w-24 rounded-md border border-line px-2"
          />
        </>
      ) : null}
    </div>
  );
}

function PdfTab({
  record,
  content,
  dirty,
}: {
  record: ReportRecord;
  content: ReportContent;
  dirty: boolean;
}) {
  const ws = useWorkspace();
  const [state, setState] = useState<{ pdf: Uint8Array; hasQr: boolean; ms: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      try {
        const r = await renderReportPdf(ws.db, ws.orgId, ws.auth.orgName, ws.auth.verifyBaseUrl, {
          labNo: record.labNo,
          status: record.status,
          verifyCode: record.verifyCode,
          updatedAt: record.updatedAt,
          version: record.version,
          content,
        });
        if (mine === seq.current) {
          setState(r);
          setError(null);
        }
      } catch (err) {
        if (mine === seq.current)
          setError(err instanceof Error ? err.message : 'The PDF could not be made');
      }
    }, 350);
    return () => clearTimeout(t);
  }, [ws.db, ws.orgId, ws.auth.orgName, ws.auth.verifyBaseUrl, record, content]);

  const url = useMemo(
    () =>
      state
        ? URL.createObjectURL(new Blob([state.pdf as BlobPart], { type: 'application/pdf' }))
        : null,
    [state],
  );
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url]);
  const fileName = `${record.labNo}-${content.patient.name.replace(/[^\w]+/g, '-')}.pdf`;

  function print() {
    if (!url) return;
    if (isDesktop() && state) {
      void printPdf(state.pdf, loadProfile()).then((r) => {
        if (!r.ok) setError(r.error ?? 'The report could not be printed');
      });
      return;
    }
    const frame = document.createElement('iframe');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
    frame.src = url;
    frame.onload = () => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      setTimeout(() => frame.remove(), 60_000);
    };
    document.body.appendChild(frame);
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        <a
          href={url ?? undefined}
          download={fileName}
          aria-disabled={!url}
          className={clsx(
            'inline-flex h-7 items-center gap-1.5 rounded-md border border-line bg-paper px-2 text-[13px] font-medium hover:bg-canvas',
            !url && 'pointer-events-none opacity-50',
          )}
        >
          <Download size={14} /> Download
        </a>
        <Button size="sm" onClick={print} disabled={!url}>
          <Printer size={14} /> Print
        </Button>
        <span className="text-[12px] text-slate" role="status">
          {error ? (
            <span className="text-flag">{error}</span>
          ) : dirty ? (
            'Updating…'
          ) : state ? (
            state.hasQr ? (
              'With verification QR'
            ) : (
              'No QR yet'
            )
          ) : (
            'Preparing…'
          )}
        </span>
      </div>
      {state && !state.hasQr && (record.status === 'final' || record.status === 'partial') ? (
        <p
          className="border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-[12px] text-warn"
          role="status"
        >
          This copy has no QR yet because the report has not reached the server. You can print it
          now. Once you are online it gets a QR, and you can print that copy for the patient.
        </p>
      ) : null}
      {state ? (
        <PdfPages pdf={state.pdf} />
      ) : (
        <div className="grid flex-1 place-items-center text-slate">Preparing the PDF…</div>
      )}
    </div>
  );
}
